import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GestureIcon from '@mui/icons-material/Gesture';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import TitleIcon from '@mui/icons-material/Title';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useT } from '../i18n/useT.js';

/** Auto-collected session context shown on the report and copied into the ticket. */
export interface TesterContext {
  url?: string;
  browser?: string;
  os?: string;
  jsErrors?: number;
}

type Tool = 'pen' | 'rect' | 'ellipse' | 'arrow' | 'text' | 'move';
interface Shape {
  tool: Tool;
  color: string;
  points: { x: number; y: number }[]; // pen: path; rect/arrow: [start, end]; text: [anchor]
  text?: string;
}

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'];

/**
 * marker.io-style annotate-and-report. Shows the captured replay frame, lets a
 * tester draw boxes / arrows / freehand over it, then composites the annotated
 * image and submits it (with the auto-collected context) as a ticket.
 */
export function TesterModeDialog({
  open,
  onClose,
  background,
  context,
  defaultTitle,
  submitting,
  error,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  /** Data URL of the captured replay frame (the annotation backdrop). */
  background: string | null;
  context: TesterContext;
  defaultTitle: string;
  submitting: boolean;
  error?: string | null;
  onSubmit: (input: { title: string; description: string; screenshot: string }) => void;
}) {
  const all = useT();
  const c = all.replay.tester;
  const common = all.common;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const drawing = useRef<Shape | null>(null);
  /** Active drag while the Move tool holds a shape. */
  const moving = useRef<{ index: number; last: { x: number; y: number } } | null>(null);
  /** When the text entry box opened — blurs in the first instant are spurious. */
  const entryOpenedAt = useRef(0);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [tool, setTool] = useState<Tool>('arrow');
  const [color, setColor] = useState(COLORS[0]);
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState('');
  // In-place text entry: viewport position for the input + the canvas anchor.
  const [textEntry, setTextEntry] = useState<{ left: number; top: number; cx: number; cy: number; value: string } | null>(null);

  useEffect(() => {
    if (open) {
      setShapes([]);
      setTitle(defaultTitle);
      setDescription('');
    }
  }, [open, defaultTitle]);

  // Load the backdrop into the canvas once it (and the dialog) are ready.
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !background) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth || 1280;
      canvas.height = img.naturalHeight || 720;
      imgRef.current = img;
      redraw([]);
    };
    img.src = background;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background]);

  useEffect(() => {
    if (open) initCanvas();
  }, [open, initCanvas]);

  const strokeWidth = () => Math.max(2, Math.round((canvasRef.current?.width ?? 1280) / 320));
  const fontSize = () => Math.max(16, Math.round((canvasRef.current?.width ?? 1280) / 40));

  const drawShape = (ctx: CanvasRenderingContext2D, s: Shape) => {
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = strokeWidth();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    if (s.tool === 'text') {
      const p = s.points[0];
      if (p && s.text) {
        const fs = fontSize();
        ctx.font = `700 ${fs}px system-ui, sans-serif`;
        ctx.textBaseline = 'top';
        s.text.split('\n').forEach((line, i) => ctx.fillText(line, p.x, p.y + i * fs * 1.25));
      }
      return;
    }
    if (s.tool === 'pen') {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else if (s.points.length >= 2) {
      const a = s.points[0];
      const b = s.points[s.points.length - 1];
      if (s.tool === 'rect') {
        ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
      } else if (s.tool === 'ellipse') {
        // Circle the area: ellipse inscribed in the dragged bounding box.
        ctx.beginPath();
        ctx.ellipse(
          (a.x + b.x) / 2,
          (a.y + b.y) / 2,
          Math.abs(b.x - a.x) / 2,
          Math.abs(b.y - a.y) / 2,
          0,
          0,
          Math.PI * 2,
        );
        ctx.stroke();
      } else {
        // arrow
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        const ang = Math.atan2(b.y - a.y, b.x - a.x);
        const head = strokeWidth() * 4;
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 6), b.y - head * Math.sin(ang - Math.PI / 6));
        ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 6), b.y - head * Math.sin(ang + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      }
    }
  };

  const redraw = (list: Shape[]) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (imgRef.current) ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    for (const s of list) drawShape(ctx, s);
    if (drawing.current) drawShape(ctx, drawing.current);
  };

  const toCanvasPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  /** Bounding box of a shape (text gets an estimated extent), padded for grabbing. */
  const shapeBBox = (s: Shape) => {
    let xs = s.points.map((p) => p.x);
    let ys = s.points.map((p) => p.y);
    if (s.tool === 'text' && s.text) {
      const fs = fontSize();
      const lines = s.text.split('\n');
      xs = [xs[0]!, xs[0]! + Math.max(...lines.map((l) => l.length)) * fs * 0.6];
      ys = [ys[0]!, ys[0]! + lines.length * fs * 1.25];
    }
    const pad = strokeWidth() * 4;
    return {
      x1: Math.min(...xs) - pad,
      y1: Math.min(...ys) - pad,
      x2: Math.max(...xs) + pad,
      y2: Math.max(...ys) + pad,
    };
  };

  /** Topmost (most recently drawn) shape under the point, or -1. */
  const hitShape = (p: { x: number; y: number }): number => {
    for (let i = shapes.length - 1; i >= 0; i--) {
      const b = shapeBBox(shapes[i]!);
      if (p.x >= b.x1 && p.x <= b.x2 && p.y >= b.y1 && p.y <= b.y2) return i;
    }
    return -1;
  };

  const onDown = (e: React.PointerEvent) => {
    if (tool === 'text') {
      // Without preventDefault, the browser's own mousedown focus-shift lands
      // AFTER the entry box autofocuses — instantly blurring it, which
      // committed the empty text and made typing impossible.
      e.preventDefault();
      const p = toCanvasPoint(e);
      // Clicking a new spot while a box is open commits what was typed there —
      // otherwise the text silently vanished unless the tester pressed Enter.
      if (textEntry?.value.trim()) {
        const t = textEntry;
        setShapes((prev) => [...prev, { tool: 'text', color, points: [{ x: t.cx, y: t.cy }], text: t.value }]);
      }
      entryOpenedAt.current = Date.now();
      setTextEntry({ left: e.clientX, top: e.clientY, cx: p.x, cy: p.y, value: '' });
      return;
    }
    if (tool === 'move') {
      const p = toCanvasPoint(e);
      const index = hitShape(p);
      if (index >= 0) {
        (e.target as Element).setPointerCapture?.(e.pointerId);
        moving.current = { index, last: p };
      }
      return;
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawing.current = { tool, color, points: [toCanvasPoint(e)] };
  };
  const commitText = () => {
    if (textEntry && textEntry.value.trim()) {
      const t = textEntry;
      setShapes((prev) => [...prev, { tool: 'text', color, points: [{ x: t.cx, y: t.cy }], text: t.value }]);
    }
    setTextEntry(null);
  };
  const onMove = (e: React.PointerEvent) => {
    if (moving.current) {
      const p = toCanvasPoint(e);
      const { index, last } = moving.current;
      const dx = p.x - last.x;
      const dy = p.y - last.y;
      moving.current.last = p;
      setShapes((prev) =>
        prev.map((s, i) =>
          i === index ? { ...s, points: s.points.map((q) => ({ x: q.x + dx, y: q.y + dy })) } : s,
        ),
      );
      return;
    }
    if (!drawing.current) return;
    const p = toCanvasPoint(e);
    if (drawing.current.tool === 'pen') drawing.current.points.push(p);
    else drawing.current.points[1] = p;
    redraw(shapes);
  };
  const onUp = () => {
    moving.current = null;
    if (!drawing.current) return;
    const s = drawing.current;
    drawing.current = null;
    const meaningful = s.tool === 'pen' ? s.points.length > 1 : s.points.length >= 2;
    if (meaningful) setShapes((prev) => [...prev, s]);
    else redraw(shapes);
  };

  useEffect(() => {
    redraw(shapes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes]);

  const submit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // A text box still open at submit time is part of the annotation — bake it
    // into the capture synchronously (state commits would race toDataURL).
    if (textEntry?.value.trim()) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        drawShape(ctx, { tool: 'text', color, points: [{ x: textEntry.cx, y: textEntry.cy }], text: textEntry.value });
      }
    }
    // The canvas already holds background + annotations composited together.
    const screenshot = canvas.toDataURL('image/jpeg', 0.72);
    onSubmit({ title: title.trim() || defaultTitle, description: description.trim(), screenshot });
  };

  const ctxChips: { label: string; value?: string | number }[] = [
    { label: c.context.url, value: context.url },
    { label: c.context.browser, value: context.browser },
    { label: c.context.os, value: context.os },
    { label: c.context.jsErrors, value: context.jsErrors },
  ];

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullScreen>
      <Stack direction="row" sx={{ height: '100%' }}>
        {/* Canvas stage */}
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#1e1e1e', display: 'grid', placeItems: 'center', p: 2, overflow: 'auto' }}>
          {background ? (
            <canvas
              ref={canvasRef}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              style={{ maxWidth: '100%', maxHeight: '100%', cursor: tool === 'text' ? 'text' : tool === 'move' ? 'move' : 'crosshair', boxShadow: '0 4px 24px rgba(0,0,0,.5)', touchAction: 'none' }}
            />
          ) : (
            <Typography color="grey.500">Capturing the current frame…</Typography>
          )}
          {textEntry && (
            <input
              autoFocus
              value={textEntry.value}
              onChange={(e) => setTextEntry((t) => (t ? { ...t, value: e.target.value } : t))}
              onBlur={(e) => {
                // A blur right after opening is the tail of the opening click,
                // not the tester leaving the box — refocus instead of commit.
                if (Date.now() - entryOpenedAt.current < 300) e.target.focus();
                else commitText();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitText(); }
                if (e.key === 'Escape') setTextEntry(null);
              }}
              placeholder={c.typeThenEnter}
              style={{
                position: 'fixed',
                left: textEntry.left,
                top: textEntry.top,
                zIndex: 2000,
                font: '700 15px system-ui, sans-serif',
                color: color,
                background: 'rgba(255,255,255,.95)',
                border: `2px solid ${color}`,
                borderRadius: 4,
                padding: '2px 6px',
                outline: 'none',
              }}
            />
          )}
        </Box>

        {/* Report panel */}
        <Box sx={{ width: 360, flexShrink: 0, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, pb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BugReportIcon color="primary" />
              <Typography variant="h6">Tester mode</Typography>
            </Stack>
            <IconButton onClick={onClose} disabled={submitting} size="small"><CloseIcon /></IconButton>
          </Stack>
          <Divider />

          <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {/* Tools */}
            <Typography variant="caption" color="text.secondary">Annotate</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={tool}
                onChange={(_, v) => {
                  if (!v) return;
                  // Leaving the text tool commits (or closes) any open entry box.
                  if (tool === 'text' && v !== 'text') commitText();
                  setTool(v);
                }}
              >
                <ToggleButton value="arrow"><Tooltip title={c.tools.arrow}><NorthEastIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="rect"><Tooltip title={c.tools.box}><CropSquareIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="ellipse"><Tooltip title={c.tools.circle}><RadioButtonUncheckedIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="pen"><Tooltip title={c.tools.pen}><GestureIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="text"><Tooltip title={c.tools.text}><TitleIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="move"><Tooltip title={c.tools.move}><OpenWithIcon fontSize="small" /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" spacing={0.5}>
                {COLORS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setColor(c)}
                    sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: color === c ? '2px solid #fff' : '2px solid transparent', outline: color === c ? `2px solid ${c}` : 'none' }}
                  />
                ))}
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title={c.tools.undo}><span><IconButton size="small" disabled={!shapes.length} onClick={() => setShapes((p) => p.slice(0, -1))}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
              <Tooltip title={c.tools.clear}><span><IconButton size="small" disabled={!shapes.length} onClick={() => setShapes([])}><DeleteSweepIcon fontSize="small" /></IconButton></span></Tooltip>
            </Stack>

            <TextField fullWidth size="small" label={c.title} value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label={c.whatsWrong} placeholder={c.describe} value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={4} sx={{ mb: 2 }} />

            <Typography variant="caption" color="text.secondary">Captured context</Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {ctxChips.filter((c) => c.value !== undefined && c.value !== '').map((c) => (
                <Stack key={c.label} direction="row" spacing={1}>
                  <Chip size="small" label={c.label} sx={{ minWidth: 74 }} />
                  <Typography variant="caption" sx={{ alignSelf: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(c.value)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>

          <Divider />
          <Stack direction="row" spacing={1} sx={{ p: 2 }}>
            <Button fullWidth onClick={onClose} disabled={submitting}>{common.cancel}</Button>
            <Button fullWidth variant="contained" startIcon={<BugReportIcon />} disabled={submitting || !background} onClick={submit}>
              {submitting ? c.creating : c.create}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Dialog>
  );
}
